import { useEffect, useRef, useState } from 'react';
import { Check, ChevronLeft, Gift, History, WalletCards } from 'lucide-react';
import { fetchVipRewardsHistory } from '../../../services/vipRewards';
import VipPageHeader from './VipPageHeader';

export function claimTime(value) {
  if (!value) return null;
  // Historical server datetimes without an offset are UTC, never phone-local.
  const normalized = typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(value) && !/(Z|[+-]\d{2}:?\d{2})$/i.test(value) ? `${value}Z` : value;
  const date = new Date(normalized);
  return Number.isNaN(date.getTime()) ? null : date;
}

const amount = (value) => new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 }).format(Number(value) || 0);

export default function VipRewardsHistory({ onBack }) {
  const [rows, setRows] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const mounted = useRef(false);
  const inFlight = useRef(false);
  const generation = useRef(0);
  const heading = useRef(null);

  const load = async (before = null) => {
    if (inFlight.current) return;
    const version = ++generation.current;
    inFlight.current = true;
    setLoading(true);
    setError(false);
    try {
      const data = await fetchVipRewardsHistory({ before, limit: 20 });
      if (!mounted.current || generation.current !== version) return;
      if (!Array.isArray(data.items)) throw new Error('Incomplete history response');
      const items = data.items.filter(row => row.status === 'claimed');
      setRows(previous => before ? [...new Map([...previous, ...items].map(row => [row.id, row])).values()] : items);
      setCursor(data.next_cursor || null);
    } catch {
      if (mounted.current && generation.current === version) setError(true);
    } finally {
      if (mounted.current && generation.current === version) { setLoading(false); inFlight.current = false; }
    }
  };

  useEffect(() => {
    mounted.current = true;
    heading.current?.focus({ preventScroll: true });
    load();
    return () => { mounted.current = false; inFlight.current = false; generation.current += 1; };
    // This component is keyed by authenticated tenant/player and has no shared cache.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <div className="player-app vip-page min-h-screen text-white" data-testid="vip-history-page">
    <VipPageHeader><div className="vip-header-inner">
      <button type="button" className="vip-back" onClick={onBack} aria-label="Back to VIP Rewards"><ChevronLeft size={22} /></button>
      <div className="vip-heading"><h1 ref={heading} tabIndex={-1}>Reward history</h1><p>Your completed VIP claims</p></div>
      <History size={24} className="vip-history-mark" aria-hidden="true" />
    </div></VipPageHeader>
    <main className="vip-main vip-history-main">
      <div className="vip-history-intro"><h2>Claimed rewards</h2><p>Newest claims first. Dates and times are shown in your local timezone.</p></div>
      {loading && rows.length === 0 && <div role="status" aria-label="Loading reward history" className="vip-loading">{[1, 2, 3, 4].map(key => <div key={key} className="vip-skeleton vip-skeleton-history" />)}</div>}
      {rows.length > 0 && <ol className="vip-history-list" aria-label="Claimed VIP rewards">
        {rows.map(row => {
          const cash = row.type === 'cashback';
          const date = claimTime(row.claimed_at);
          const Icon = cash ? WalletCards : Gift;
          return <li key={row.id} className="vip-history-row">
            <span className={`vip-history-icon ${cash ? 'is-cash' : ''}`}><Icon size={21} aria-hidden="true" /></span>
            <div className="vip-history-copy"><h3>{row.title || (cash ? 'Wallet Cashback' : 'Freeplay Load Bonus')}</h3>
              {date ? <time dateTime={date.toISOString()}>{new Intl.DateTimeFormat(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }).format(date)}</time> : <span className="vip-history-time">Claim time unavailable</span>}
              <span className="vip-claimed-status"><Check size={12} aria-hidden="true" /> Claimed</span>
            </div>
            <div className="vip-history-amount"><strong>{cash ? `$${Number(row.reward_cash ?? row.reward_fp ?? 0).toFixed(2)}` : `${amount(row.reward_fp)} FP`}</strong><span>{cash ? 'Main Wallet' : 'Freeplay'}</span></div>
          </li>;
        })}
      </ol>}
      {!loading && !error && rows.length === 0 && <div className="vip-empty"><History size={32} /><h2>No claims yet</h2><p>Once you claim a VIP reward, its amount and claim time will appear here.</p><button type="button" className="vip-secondary" onClick={onBack}>View rewards</button></div>}
      {error && <div className="vip-history-error" role="alert"><p>Could not load {rows.length ? 'more ' : ''}reward history. Your claims have not changed.</p><button type="button" className="vip-secondary" onClick={() => load(cursor)}>Try again</button></div>}
      {cursor && !error && <button type="button" className="vip-load-more" disabled={loading} onClick={() => load(cursor)}>{loading ? 'Loading…' : 'Load more'}</button>}
      {!loading && !error && !cursor && rows.length > 0 && <p className="vip-history-end">You’re up to date · All completed claims shown</p>}
    </main>
  </div>;
}
